import React, { useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import ManualQuestionCreator from '../components/ManualQuestionCreator';

const QUESTION_BANK_VIEW_MODE_KEY = 'questionBank_viewMode';
const QUESTION_BANK_PAGE_STATE_KEY = 'questionBank_pageState';
const QUESTION_BANK_SCROLL_KEY = 'questionBank_scrollY';

const getStoredQuestionBankState = () => {
  try {
    const rawState = localStorage.getItem(QUESTION_BANK_PAGE_STATE_KEY);
    if (!rawState) return null;
    const parsed = JSON.parse(rawState);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const QuestionBankPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isStudent = location.pathname.startsWith('/students/dashboard');
  const isTeacher = location.pathname.startsWith('/teachers/dashboard');
  const isAdmin = user?.role === 'admin';
  const isPastQuestionsMode = location.pathname.includes('/past-questions');
  
  const [questions, setQuestions] = useState([]);
  const [filters, setFilters] = useState(() => {
    const storedState = getStoredQuestionBankState();
    return storedState?.filters || { year: '', subject: '', topic: '', section: '' };
  });
  const [trigger, setTrigger] = useState(0);
  // Load settings from localStorage or default to empty
  const [questionsPerPage, setQuestionsPerPage] = useState(() => {
    const storedState = getStoredQuestionBankState();
    return storedState?.questionsPerPage ?? localStorage.getItem('questionBank_questionsPerPage') ?? '';
  });
  const [timeLimit, setTimeLimit] = useState(() => {
    const storedState = getStoredQuestionBankState();
    return storedState?.timeLimit ?? localStorage.getItem('questionBank_timeLimit') ?? '';
  });
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => {
    const storedState = getStoredQuestionBankState();
    return storedState?.searchTerm || '';
  });
  const [viewMode, setViewMode] = useState(() => {
    const storedState = getStoredQuestionBankState();
    if (storedState?.viewMode === 'table' || storedState?.viewMode === 'cards') {
      return storedState.viewMode;
    }
    const storedViewMode = localStorage.getItem(QUESTION_BANK_VIEW_MODE_KEY);
    return storedViewMode === 'table' || storedViewMode === 'cards' ? storedViewMode : 'cards';
  }); // 'cards' or 'table'
  const [showManualCreator, setShowManualCreator] = useState(false);

  const getReadPath = (id) => {
    if (isPastQuestionsMode) {
      if (isAdmin) return `/admin/dashboard/past-questions/read/${id}`;
      if (isTeacher) return `/teachers/dashboard/past-questions/read/${id}`;
      return `/students/dashboard/past-questions/read/${id}`;
    }

    if (isAdmin) return `/admin/dashboard/questions/read/${id}`;
    if (isStudent) return `/students/dashboard/past-questions/read/${id}`;
    return `/teachers/dashboard/questions/read/${id}`;
  };

  const getAttemptPath = (id) => {
    if (isPastQuestionsMode) {
      // Past questions are read-only for admin/teacher.
      if (isAdmin || isTeacher) return '';
      return `/students/dashboard/past-questions/attempt/${id}`;
    }

    if (isAdmin) return `/admin/dashboard/questions/attempt/${id}`;
    if (isStudent) return `/students/dashboard/past-questions/attempt/${id}`;
    return '';
  };

  useEffect(() => { 
    fetchQuestions(); 
  }, [trigger]);

  // Save questionsPerPage to localStorage whenever it changes
  useEffect(() => {
    if (questionsPerPage !== '') {
      localStorage.setItem('questionBank_questionsPerPage', questionsPerPage);
    }
  }, [questionsPerPage]);

  // Save timeLimit to localStorage whenever it changes
  useEffect(() => {
    if (timeLimit !== '') {
      localStorage.setItem('questionBank_timeLimit', timeLimit);
    }
  }, [timeLimit]);

  // Persist card/table preference so it doesn't switch on refresh.
  useEffect(() => {
    localStorage.setItem(QUESTION_BANK_VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Persist full page state so users can come back where they left off.
  useEffect(() => {
    const payload = {
      filters,
      searchTerm,
      questionsPerPage,
      timeLimit,
      viewMode,
      mode: isPastQuestionsMode ? 'past-questions' : 'question-bank',
      path: location.pathname,
      updatedAt: Date.now(),
    };

    localStorage.setItem(QUESTION_BANK_PAGE_STATE_KEY, JSON.stringify(payload));
  }, [filters, searchTerm, questionsPerPage, timeLimit, viewMode, isPastQuestionsMode, location.pathname]);

  // Keep last scroll so refresh/back navigation returns to same spot.
  useEffect(() => {
    const saveScroll = () => {
      localStorage.setItem(QUESTION_BANK_SCROLL_KEY, String(window.scrollY || 0));
    };

    window.addEventListener('scroll', saveScroll, { passive: true });
    return () => {
      saveScroll();
      window.removeEventListener('scroll', saveScroll);
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      const savedScroll = Number(localStorage.getItem(QUESTION_BANK_SCROLL_KEY));
      if (Number.isFinite(savedScroll) && savedScroll > 0) {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: savedScroll, behavior: 'auto' });
        });
      }
      setLoadingProgress(0);
      return;
    }

    setLoadingProgress(8);
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 95) return prev;
        const step = Math.max(1, Math.round((100 - prev) / 12));
        return Math.min(95, prev + step);
      });
    }, 160);

    return () => clearInterval(interval);
  }, [loading]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setLoadingProgress(12);
      setError(null);
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const endpoint = isPastQuestionsMode ? '/questions/public/past-questions' : '/questions';
      if (isPastQuestionsMode) {
        params.includeAnswer = 'true';
        // For some integrations we need the full dataset (including answers).
        // Set `all=true` so the backend will aggregate across provider pages
        // and return a single consolidated response. Note: this can trigger
        // heavier provider requests and may be rate-limited by upstream.
        params.all = 'true';
      }
      const res = await api.get(endpoint, { params });
      const payload = res.data;
      if (Array.isArray(payload)) {
        setQuestions(payload);
      } else if (Array.isArray(payload?.questions)) {
        setQuestions(payload.questions);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err.message || 'Failed to fetch questions');
      setQuestions([]);
    } finally {
      setLoadingProgress(100);
      setLoading(false);
    }
  };

  const getBadgeColor = (type) => {
    switch (type) {
      case 'shs1': return 'bg-green-100 text-green-800 border-green-200';
      case 'shs2': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shs3': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDisplayName = (type) => {
    switch (type) {
      case 'shs1': return 'SHS 1';
      case 'shs2': return 'SHS 2';
      case 'shs3': return 'SHS 3';
      default: return type;
    }
  };

  const getSubjectIcon = (subject) => {
    const subjectLower = subject?.toLowerCase() || '';
    if (subjectLower.includes('math')) return '🔢';
    if (subjectLower.includes('english')) return '📝';
    if (subjectLower.includes('science')) return '🔬';
    if (subjectLower.includes('social')) return '🌍';
    if (subjectLower.includes('french')) return '🇫🇷';
    if (subjectLower.includes('religious')) return '📿';
    if (subjectLower.includes('technology') || subjectLower.includes('design')) return '⚙️';
    return '📚';
  };

  const groups = useMemo(() => {
    const m = {};
    questions.forEach(q => {
      const key = `${q.year}-${q.subject}-${q.section}`;
      if (!m[key]) m[key] = [];
      m[key].push(q);
    });
    return Object.entries(m).map(([k, qs]) => {
      const [year, subject, section] = k.split('-');
      return { year, subject, section, questions: qs };
    }).filter(group => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        group.year?.toLowerCase().includes(searchLower) ||
        group.subject?.toLowerCase().includes(searchLower) ||
        group.section?.toLowerCase().includes(searchLower)
      );
    });
  }, [questions, searchTerm]);

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete ${group.questions.length} questions for ${group.year} ${group.subject} section ${group.section}? This action cannot be undone.`)) return;
    try {
      await Promise.all(group.questions.map(q => api.delete(`/questions/${q.id}`)));
      fetchQuestions();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleEditGroup = (group) => {
    // Navigate to edit page with group data
    navigate('/admin/dashboard/questions/edit', { state: { group } });
  };

  const QuestionGroupCard = ({ group }) => (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 sm:hover:scale-[1.01] group">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-center space-x-3 min-w-0">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow-lg">
            <span className="text-2xl text-white">{getSubjectIcon(group.subject)}</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{group.subject}</h3>
            <p className="text-sm text-gray-500">Year {group.year} • Section {group.section}</p>
          </div>
        </div>
        <div className="sm:text-right">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            {group.questions.length} questions
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600">Academic Year</div>
          <div className="font-semibold text-gray-900">{group.year}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600">Section</div>
          <div className="font-semibold text-gray-900">{group.section}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-3">
        <button
          className="w-full sm:flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
          onClick={() => {
            const id = group.questions[0].id;
            const path = getReadPath(id);
            navigate(path, { state: { questions: group.questions, meta: group } });
          }}
        >
          <span>👁️</span>
          <span>Read</span>
        </button>
        
        {Boolean(getAttemptPath(group.questions[0].id)) && (
          <button
            className="w-full sm:flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
            onClick={() => {
              const id = group.questions[0].id;
              const attemptPath = getAttemptPath(id);
              if (attemptPath) {
                navigate(attemptPath, { state: { questions: group.questions, meta: { ...group, questionsPerPage: parseInt(questionsPerPage) || null, timeLimit: parseInt(timeLimit) || null } } });
              }
            }}
          >
            <span>🎯</span>
            <span>Take Quiz</span>
          </button>
        )}
        
  {isAdmin && !isPastQuestionsMode && (
          <>
            <button
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
              onClick={() => handleEditGroup(group)}
            >
              <span>✏️</span>
              <span>Edit</span>
            </button>
            
            <button
              className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
              onClick={() => handleDeleteGroup(group)}
            >
              <span>🗑️</span>
              <span>Delete</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  QuestionGroupCard.propTypes = {
    group: PropTypes.shape({
      year: PropTypes.string.isRequired,
      subject: PropTypes.string.isRequired,
      section: PropTypes.string.isRequired,
      questions: PropTypes.array.isRequired
    }).isRequired
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
              <div className="absolute top-0 left-0 rounded-full h-16 w-16 border-t-4 border-indigo-300 animate-pulse"></div>
            </div>
            <p className="mt-6 text-lg text-gray-700 font-semibold text-center">
              {isPastQuestionsMode ? 'Loading your past questions, please wait.' : 'Loading questions, please wait.'}
            </p>
            <div className="mt-4 w-full max-w-sm">
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-all duration-300 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500 text-center">{loadingProgress}%</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Questions</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                fetchQuestions();
              }} 
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <span className="text-3xl sm:text-4xl">📚</span>
                <span>Question Bank</span>
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Browse and practice with our collection of past questions</p>
              {isStudent && user?.studentType && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium border mt-2 ${getBadgeColor(user.studentType)}`}>
                  {getDisplayName(user.studentType)} Student
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium text-sm text-center sm:text-left">
                {groups.length} question groups
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowManualCreator(true)}
                  className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
                >
                  <span>➕</span>
                  <span>Create Question</span>
                </button>
              )}
              <button
                onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
                className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 shadow-sm flex items-center justify-center space-x-2"
              >
                <span>{viewMode === 'cards' ? '📋' : '🎴'}</span>
                <span>{viewMode === 'cards' ? 'Table View' : 'Card View'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Search & Filters</h2>
          
          {/* Search Bar */}
          <div className="mb-4">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">Search Questions</label>
            <input
              id="search"
              type="text"
              placeholder="Search by year, subject, or section..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          {/* Filter Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            {['year', 'subject', 'topic', 'section'].map(field => (
              <div key={field}>
                <label htmlFor={field} className="block text-sm font-medium text-gray-700 mb-2 capitalize">{field}</label>
                <input
                  id={field}
                  placeholder={`Filter by ${field}...`}
                  value={filters[field]}
                  onChange={e => setFilters({ ...filters, [field]: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            ))}
          </div>

          {/* Quiz Settings */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label htmlFor="questions-per-page" className="block text-sm font-medium text-gray-700 mb-2">Questions Per Page</label>
              <input
                id="questions-per-page"
                type="number"
                placeholder="e.g. 10"
                value={questionsPerPage}
                onChange={e => setQuestionsPerPage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              />
            </div>
            <div>
              <label htmlFor="time-limit" className="block text-sm font-medium text-gray-700 mb-2">Time Limit (minutes)</label>
              <input
                id="time-limit"
                type="number"
                placeholder="e.g. 30"
                value={timeLimit}
                onChange={e => setTimeLimit(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ year: '', subject: '', topic: '', section: '' });
                  setSearchTerm('');
                  setQuestionsPerPage('');
                  setTimeLimit('');
                  // Clear from localStorage as well
                  localStorage.removeItem('questionBank_questionsPerPage');
                  localStorage.removeItem('questionBank_timeLimit');
                  localStorage.removeItem(QUESTION_BANK_PAGE_STATE_KEY);
                  localStorage.removeItem(QUESTION_BANK_SCROLL_KEY);
                }}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {groups.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Questions Found</h3>
            <p className="text-gray-600">No questions match your current search and filters.</p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {groups.map((group) => (
              <QuestionGroupCard key={`${group.year}-${group.subject}-${group.section}`} group={group} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="block md:hidden divide-y divide-gray-200">
              {groups.map((group, idx) => (
                <div key={idx} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500">{group.year} • Section {group.section}</p>
                      <h3 className="font-semibold text-gray-900 truncate">{getSubjectIcon(group.subject)} {group.subject}</h3>
                    </div>
                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {group.questions.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm font-medium">
                    <button
                      className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                      onClick={() => {
                        const id = group.questions[0].id;
                        const path = getReadPath(id);
                        navigate(path, { state: { questions: group.questions, meta: group } });
                      }}
                    >
                      Read
                    </button>
                    {Boolean(getAttemptPath(group.questions[0].id)) && (
                      <button
                        className="text-green-600 hover:text-green-900 transition-colors duration-200"
                        onClick={() => {
                          const id = group.questions[0].id;
                          const attemptPath = getAttemptPath(id);
                          if (attemptPath) {
                            navigate(attemptPath, { state: { questions: group.questions, meta: { ...group, questionsPerPage: parseInt(questionsPerPage) || null, timeLimit: parseInt(timeLimit) || null } } });
                          }
                        }}
                      >
                        Take Quiz
                      </button>
                    )}
                    {isAdmin && !isPastQuestionsMode && (
                      <button
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        onClick={() => handleDeleteGroup(group)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groups.map((group, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{getSubjectIcon(group.subject)}</span>
                          <span className="text-sm font-medium text-gray-900">{group.subject}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{group.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{group.section}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {group.questions.length} questions
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                          onClick={() => {
                            const id = group.questions[0].id;
                            const path = getReadPath(id);
                            navigate(path, { state: { questions: group.questions, meta: group } });
                          }}
                        >
                          Read
                        </button>
                        {Boolean(getAttemptPath(group.questions[0].id)) && (
                          <button
                            className="text-green-600 hover:text-green-900 transition-colors duration-200"
                            onClick={() => {
                              const id = group.questions[0].id;
                              const attemptPath = getAttemptPath(id);
                              if (attemptPath) {
                                navigate(attemptPath, { state: { questions: group.questions, meta: { ...group, questionsPerPage: parseInt(questionsPerPage) || null, timeLimit: parseInt(timeLimit) || null } } });
                              }
                            }}
                          >
                            Take Quiz
                          </button>
                        )}
                        {isAdmin && !isPastQuestionsMode && (
                          <button
                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                            onClick={() => handleDeleteGroup(group)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Manual Question Creator Modal */}
      {showManualCreator && (
        <ManualQuestionCreator
          onQuestionCreated={() => {
            setTrigger(t => t + 1); // Refresh questions list
          }}
          onClose={() => setShowManualCreator(false)}
        />
      )}
    </div>
  );
};

export default QuestionBankPage;
